'use client';

import { useParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { Button } from 'react-aria-components';

import ListingFilterNew from '@/features/listing/components/listings-filters/listing-filter-new/listing-filter-new';
import { LISTING_URL_PART_VARIANTS } from '@/features/listing/constants/listing-constants';
import { LISTINGS_TYPE_FILTER } from '@/features/listing/constants/listings-filters-constants';
import { setCookieServiceType } from '@/features/listing/cookies/cookie-service-type';
import { type ListingsFilterName } from '@/features/listing/types/listing-filters-types';
import {
  type ListingType,
  type ListingUrl,
} from '@/features/listing/types/listing-types';
import { useRouter } from '@/i18n/routing';

import styles from './listing-type-filter.module.scss';

interface ListingTypeFilterProps {
  onFilterDisplayChange: (filterName: ListingsFilterName | null) => void;
  filterLink: {
    pathname: string;
    searchParams: URLSearchParams;
  }
}

export default function ListingTypeFilter(props: ListingTypeFilterProps) {
  const {
    filterLink,
    onFilterDisplayChange,
  } = props;

  const translationsFilterType = useTranslations('listing.filters.type');

  const params = useParams();
  const router = useRouter();

  const currentListingType = LISTING_URL_PART_VARIANTS[params.listingType as ListingUrl];

  const [isPopoverOpen,
    setPopoverOpen] = useState(false);

  const handleChange = (value: ListingType) => () => {
    const selectedListingType = LISTINGS_TYPE_FILTER.options.find((filter) => filter.value === value);

    if (selectedListingType) {
      const updatedPathname = filterLink.pathname.replace(params.listingType as string, selectedListingType.pathValue);
      router.push(updatedPathname);
      setCookieServiceType({ data: selectedListingType.value });
      setPopoverOpen(false);
    }
  };

  return (
    <ListingFilterNew
      buttonText={translationsFilterType(`options.${currentListingType}.button`)}
      dialogTitle={translationsFilterType('dropdown-title')}
      onFilterDisplayChange={onFilterDisplayChange}
      listingFilterName="listingsType"
      isPopoverOpen={isPopoverOpen}
      setPopoverOpen={setPopoverOpen}
    >
      {LISTINGS_TYPE_FILTER.options.map((option) => {
        const Icon = option.icon;

        return (
          <Button
            key={option.value}
            className={styles.filterToggle}
            onPress={handleChange(option.value)}
            isDisabled={option.value === currentListingType}
          >
            {Icon && (
              <Icon size={18} />
            )}
            <span>{translationsFilterType(`options.${option.value}.label`)}</span>
          </Button>
        );
      })}
    </ListingFilterNew>
  );
}
